import { DIR_DX, DIR_DY, positionAt } from '@enugu/shared';
import { headingToDirection } from '../iso/IsoMath';
/**
 * Path-following movement component, stepped by the fixed-rate simulation.
 *
 * Positions are tile-space floats; standing on tile (3, 4) means (3.5, 4.5).
 * `prevX/prevY` hold the previous sim state so the renderer can interpolate.
 *
 * This is also the M1 client-prediction unit: the local player moves along
 * the locally computed path immediately while the same path request goes to
 * the server, which replays it with the same rules and tick rate (docs/30 §Netcode).
 */
export class PathMover {
    x;
    y;
    prevX;
    prevY;
    /** Tile the mover last arrived at (the logical occupied tile). */
    tileX;
    tileY;
    /** Tiles per second. */
    speed;
    facing = 'SE';
    path = [];
    index = 0;
    /** Set when the next step became blocked (dynamic obstacle); the owner should re-path. */
    blocked = false;
    /** Index of the segment whose step was already validated against the grid. */
    checkedIndex = -1;
    // Timed replay (M1-12): a server mover {x, y, path, startAt, gait} in raw tile
    // coords, sampled with shared positionAt on `clock()` (ms). null = local stepping.
    timed = null;
    timedPrev = null;
    clock = null;
    timedMoving = false;
    // Optional post-sample hook (net smoothing): fn(mover, dt).
    smooth = null;
    constructor(tileX, tileY, speed = 2.5) {
        this.tileX = tileX;
        this.tileY = tileY;
        this.x = this.prevX = tileX + 0.5;
        this.y = this.prevY = tileY + 0.5;
        this.speed = speed;
    }
    get moving() {
        if (this.timed)
            return this.timedMoving;
        return this.index < this.path.length;
    }
    /** Tile a new path should start from: the tile we are walking into, or the current tile. */
    pathOrigin() {
        if (this.moving && this.checkedIndex === this.index) {
            const n = this.path[this.index];
            return { x: n.x, y: n.y };
        }
        return { x: this.tileX, y: this.tileY };
    }
    /**
     * Follows a new path that starts AFTER `pathOrigin()`. If we are mid-step,
     * the current step is kept so motion never snaps backwards.
     */
    setPath(path) {
        this.blocked = false;
        if (this.moving && this.checkedIndex === this.index) {
            const cur = this.path[this.index];
            this.path = [cur, ...path];
            this.index = 0;
            this.checkedIndex = 0;
        }
        else {
            this.path = path;
            this.index = 0;
            this.checkedIndex = -1;
        }
    }
    stop() {
        // Finish the current step (if any) so we always rest on a tile centre.
        if (this.moving && this.checkedIndex === this.index) {
            this.path = [this.path[this.index]];
            this.index = 0;
            this.checkedIndex = 0;
        }
        else {
            this.path = [];
            this.index = 0;
        }
    }
    /** Teleports to a tile (spawn, server correction). */
    place(tileX, tileY) {
        this.timed = this.timedPrev = null;
        this.timedMoving = false;
        this.tileX = tileX;
        this.tileY = tileY;
        this.x = this.prevX = tileX + 0.5;
        this.y = this.prevY = tileY + 0.5;
        this.path = [];
        this.index = 0;
        this.checkedIndex = -1;
        this.blocked = false;
    }
    /**
     * Switches to timed replay of `m` on `clock`. The mover active at time `t`
     * is kept until `m.startAt`, since a new path starts at the commit tile.
     */
    setTimed(m, clock, t = clock()) {
        const cur = this.timed;
        if (cur)
            this.timedPrev = this.timedPrev && t < cur.startAt ? this.timedPrev : cur;
        this.timed = { x: m.x, y: m.y, path: m.path, startAt: m.startAt, gait: m.gait ?? 0 };
        this.clock = clock;
        this.path = [];
        this.index = 0;
        this.checkedIndex = -1;
        this.blocked = false;
    }
    /** Replaces the timed mover without keeping the previous one (corrections, snaps). */
    resetTimed(m, clock) {
        this.timed = null;
        this.timedPrev = null;
        this.setTimed(m, clock);
    }
    /** Leaves timed mode, resting on the nearest tile. */
    clearTimed() {
        const tx = Math.floor(this.x);
        const ty = Math.floor(this.y);
        this.timed = this.timedPrev = this.clock = this.smooth = null;
        this.timedMoving = false;
        this.place(tx, ty);
    }
    /** The timed mover in effect at `t`. */
    timedAt(t) {
        return this.timedPrev && t < this.timed.startAt ? this.timedPrev : this.timed;
    }
    /** Sets position and facing from the timed mover at time `t`; returns positionAt's result. */
    replayAt(t) {
        const p = positionAt(this.timedAt(t), t);
        this.x = p.x + 0.5;
        this.y = p.y + 0.5;
        this.tileX = Math.round(p.x);
        this.tileY = Math.round(p.y);
        this.timedMoving = p.moving;
        if (p.dir >= 0)
            this.facing = headingToDirection(DIR_DX[p.dir], DIR_DY[p.dir], this.facing);
        if (this.timedPrev && t >= this.timed.startAt)
            this.timedPrev = null;
        return p;
    }
    /** One fixed sim step. */
    step(dt, grid) {
        this.prevX = this.x;
        this.prevY = this.y;
        if (this.timed) {
            this.replayAt(this.clock());
            this.smooth?.(this, dt);
            return;
        }
        let budget = this.speed * dt;
        while (budget > 1e-9 && this.index < this.path.length) {
            const node = this.path[this.index];
            if (this.checkedIndex !== this.index) {
                // About to leave the current tile: validate the step against the live grid.
                const dx = node.x - this.tileX;
                const dy = node.y - this.tileY;
                if (Math.abs(dx) > 1 || Math.abs(dy) > 1 || !grid.canStep(this.tileX, this.tileY, dx, dy)) {
                    this.blocked = true;
                    this.path = [];
                    this.index = 0;
                    this.checkedIndex = -1;
                    break;
                }
                this.checkedIndex = this.index;
            }
            const tx = node.x + 0.5;
            const ty = node.y + 0.5;
            const ddx = tx - this.x;
            const ddy = ty - this.y;
            const dist = Math.sqrt(ddx * ddx + ddy * ddy);
            if (dist > 1e-9)
                this.facing = headingToDirection(ddx, ddy, this.facing);
            if (dist <= budget) {
                this.x = tx;
                this.y = ty;
                this.tileX = node.x;
                this.tileY = node.y;
                budget -= dist;
                this.index++;
            }
            else {
                this.x += (ddx / dist) * budget;
                this.y += (ddy / dist) * budget;
                budget = 0;
            }
        }
        if (this.index >= this.path.length && this.path.length > 0) {
            this.path = [];
            this.index = 0;
            this.checkedIndex = -1;
        }
    }
}
